import { useEffect, useRef, useState } from "react";
import { serverNow } from "./store.ts";

/**
 * Odliczanie do absolutnego terminu z serwera.
 *
 * Liczymy lokalnie, ale od `deadlineAt` podanego przez serwer i skorygowanego
 * o zmierzoną różnicę zegarów. Zegary telefonów w aucie potrafią być
 * rozjechane o minuty, a runda musi kończyć się wszystkim naraz — inaczej
 * jeden gracz widzi „5 sekund", gdy inny ma jeszcze „minutę".
 */
export function useCountdown(deadlineAt: number | null): number | null {
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (deadlineAt === null) {
      setLeft(null);
      return;
    }

    const tick = () => setLeft(Math.max(0, deadlineAt - serverNow()));
    tick();
    // 200 ms zamiast sekundy: przy odświeżaniu co sekundę wyświetlana wartość
    // potrafi przeskoczyć o dwie, bo timery przeglądarki dryfują — a tuż przed
    // końcem rundy to widać i denerwuje.
    const timer = setInterval(tick, 200);
    return () => clearInterval(timer);
  }, [deadlineAt]);

  return left;
}

/** Sekundy do wyświetlenia, zaokrąglone w górę — „0" pokazuje się dopiero na koniec. */
export function toSeconds(ms: number | null): number | null {
  return ms === null ? null : Math.ceil(ms / 1000);
}

/**
 * Krótka wibracja.
 *
 * Telefon bywa w uchwycie albo w kieszeni, a start rundy i STOP to momenty,
 * których nie wolno przegapić. Brak wsparcia (iOS) nie jest błędem.
 */
export function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Wibracja jest dodatkiem, nie warunkiem działania gry.
  }
}

/**
 * Uruchamia efekt tylko przy faktycznej zmianie wartości, pomijając pierwsze
 * renderowanie. Używane do haptyki: wejście na ekran rundy nie ma wibrować,
 * ale przejście do kolejnej rundy już tak.
 */
export function useOnChange<T>(value: T, effect: (current: T, previous: T) => void): void {
  const previous = useRef<T | null>(null);
  useEffect(() => {
    if (previous.current !== null && previous.current !== value) {
      effect(value, previous.current);
    }
    previous.current = value;
  }, [value, effect]);
}

/**
 * Debounce dla wpisywanych odpowiedzi.
 *
 * Wysyłanie po każdym znaku zalałoby WebSocket w chwili, gdy pięć osób pisze
 * naraz; wysyłanie dopiero po opuszczeniu pola gubiłoby ostatnie słowo przy
 * STOP-ie. Pół sekundy trafia w środek.
 */
export function useDebounced<T extends (...args: never[]) => void>(fn: T, delayMs = 500): T {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(fn);
  latest.current = fn;

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);

  return ((...args: Parameters<T>) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => latest.current(...(args as never[])), delayMs);
  }) as T;
}
